import * as Sentry from '@sentry/react';
import React, { ReactChild, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactHowler from 'react-howler';
import { useParams, useHistory } from "react-router-dom";
import { shallow } from 'zustand/shallow'

import { PitchDropdown, SpeedSlider, StylesDropdown, VolumeSlider } from './VoiceConfigurations';
import Spinner from '../Spinner';

import { IVoice, useAIVoicesStore } from '../../zustand-store/useAIVoicesStore';
import { useSetDefaultProfile } from '../../http-hooks/integration/useSetDefaultProfile';

import playPreviewIcon from '../../assets/play_preview.svg';
import pausePreviewIcon from '../../assets/pause_preview.svg';
import { useAudioPreview } from '../../http-hooks/ai-voices/useAudioPreview';
import { useCreateProfile } from '../../http-hooks/ai-voices/useCreateProfile';
import { useVoiceConfigurationStore } from '../../zustand-store/useVoiceConfigurationStore';
import { useEditProfile } from '../../http-hooks/ai-voices/useEditProfile';
import { useSavedProfilesStore } from '../../zustand-store/useSavedProfilesStore';
import { isPitchDisabled, isSpeedDisabled, isStyleDisabled } from '../../zustand-store/useVoiceFiltersStore';

export type PageParams = {
    org_id: string;
    id: string;
};

export const SetDefaultVoiceModal = () => {

    const { org_id, id } = useParams<PageParams>();
    const mutation = useSetDefaultProfile();
    
    const { selectedVoice, show, closeModal } = useAIVoicesStore((state) => ({
        selectedVoice: state.selectedVoice, 
        show: state.showSetDefaultProfileModal,
        closeModal: state.closeModal,
    }), shallow)
    const voiceConfiguration = useVoiceConfigurationStore((state) => state.voiceConfiguration)

    if (selectedVoice === null) {
        return <></>
    }

    return (
        <ConfigureVoiceModal 
            show={show}
            title={`Configure ${selectedVoice.name} (${selectedVoice.gender.charAt(0).toUpperCase()}) Voice`}
            onHide={closeModal}
            voice={selectedVoice}
            submitButtonText="Set Default"
            isSubmitting={mutation.isLoading}
            onSubmit={async () => {
                try {
                    await mutation.mutateAsync({ org_id, id, profile: {
                ...voiceConfiguration,
                voice: selectedVoice.code,
                lang_code: selectedVoice.langCode,
                platform: selectedVoice.platform,
                engine: selectedVoice.engine
                    }});
                    toast.success("Default profile saved successfully!");
                    closeModal();
                } catch (error) {
                    toast.error("Failed to set default profile");
                }
            }}
        />
    )
}

export const EditCustomProfileModal = () => {

    const { org_id, id } = useParams<PageParams>();
    
    const { selectedProfile, show, closeModal, toggleChangeVoice } = useSavedProfilesStore((state) => ({
        selectedProfile: state.selectedProfile,
        show: state.showEditProfile,
        closeModal: state.closeModal,
        toggleChangeVoice: state.toggleChangeVoice,
    }), shallow)
    
    const editProfile = useEditProfile();
    const editDefaultProfile = useSetDefaultProfile();
    const voiceConfiguration = useVoiceConfigurationStore((state) => state.voiceConfiguration)
    const selectedVoice = useAIVoicesStore((state) => state.selectedVoice)

    if (!selectedProfile || !selectedVoice) {
        return <></>
    }

    const language = useAIVoicesStore.getState().languages[selectedVoice.langCode]

    return (
        <ConfigureVoiceModal
            show={show}
            title={`Edit Profile - ${selectedProfile.profile_name}`}
            voice={selectedVoice}
            onHide={closeModal}
            submitButtonText="Save Profile"
            isSubmitting={editProfile.isLoading || editDefaultProfile.isLoading}
            onSubmit={async () => {
                try {
                if (!selectedProfile.id) {
                        await editDefaultProfile.mutateAsync({ org_id, id, profile: {
                        ...voiceConfiguration,
                        voice: selectedVoice.code,
                        lang_code: selectedVoice.langCode,
                        platform: selectedVoice.platform,
                        engine: selectedVoice.engine
                        }});
                } else {
                        await editProfile.mutateAsync({ org_id, id, profile_id: selectedProfile.id, profile: {
                        ...selectedProfile,
                        ...voiceConfiguration,
                        voice: selectedVoice.code,
                        lang_code: selectedVoice.langCode
                        }});
                    }
                    toast.success("Profile settings changed successfully");
                    closeModal();
                } catch (error) {
                    toast.error("Failed to update profile");
                }
            }}
        >
            <div style={{ width: '100%', background: 'rgba(var(--bs-primary-rgb), 0.04)' }} className='d-flex px-3 py-2 mb-3'>
                <div style={{color: 'var(--bs-emphasis-color)'}} className='flex-grow-1'>
                    <span>Voice:</span>
                    <span style={{fontWeight: 600}}>{selectedVoice?.name}, {language.name} ({selectedVoice.gender.charAt(0).toUpperCase()})</span>
                </div>
                <button onClick={() => {
                    toggleChangeVoice()
                }} style={{ color: 'white', fontSize: 14, fontWeight: 500, borderRadius: 12, height: 24, background: 'var(--bs-primary)', border: 'none', padding: '2px 8px' }}>
                    Change Voice
                </button>
            </div>
        </ConfigureVoiceModal>
    )

}

export const CreateCustomProfileModal = () => {

    const { org_id, id } = useParams<PageParams>();
    const history = useHistory();

    const mutation = useCreateProfile();

    const { selectedVoice, show, closeModal } = useAIVoicesStore((state) => ({
        selectedVoice: state.selectedVoice, 
        show: state.showCreateCustomVoiceProfileModal, 
        closeModal: state.closeModal
    }), shallow)
    const voiceConfiguration = useVoiceConfigurationStore((state) => state.voiceConfiguration)

    const [profileName, setProfileName] = useState('')
    const [profileNameError, setProfileNameError] = useState('');

    if (selectedVoice === null) {
        return <></>
    }

    return (
        <ConfigureVoiceModal 
            show={show}
            title={`Configure ${selectedVoice.name} (${selectedVoice.gender.charAt(0).toUpperCase()}) Voice`}
            voice={selectedVoice}
            onHide={closeModal}
            submitButtonText="Create Profile"
            isSubmitting={mutation.isLoading}
            onSubmit={async () => {
                if (profileName === '') {
                    setProfileNameError('Profile name cannot be empty')
                    return;
                }
                setProfileNameError('')
                
                try {
                    await mutation.mutateAsync({ org_id, id, profile: {
                    ...voiceConfiguration,
                    profile_name: profileName,
                    voice: selectedVoice.code,
                    lang_code: selectedVoice.langCode,
                    platform: selectedVoice.platform,
                    engine: selectedVoice.engine
                    }});
                    
                    // ✨ Success actions
                    toast.success("New profile created successfully");
                    closeModal();
                    history.push(`/organisation/${org_id}/integration/${id}/profiles`);
                } catch (error) {
                    toast.error("Failed to create profile");
                }
            }}
        >
            <div className="mb-3 col-12 mb-4">
                <label className="d-inline-flex align-items-center">
                    <span>Profile name</span>
                </label>
                <input type="text" className="form-control" value={profileName} onChange={(e) => {
                    setProfileName(e.target.value)
                }} />
                {
                    profileNameError && (
                        <span style={{fontSize: 13}} className="text-danger">{profileNameError}</span>
                    )
                }
            </div>
        </ConfigureVoiceModal>
    )
}

interface IConfigureVoiceModalProps {
    show: boolean
    title: string
    voice: IVoice
    onHide: () => void
    children?: ReactChild
    onSubmit: () => void
    isSubmitting: boolean
    submitButtonText: string
}

const ConfigureVoiceModal = ({children, title, voice, onHide, isSubmitting, onSubmit, submitButtonText, show} : IConfigureVoiceModalProps) => {

    return (
        <Modal show={show} onHide={() => {
            onHide()
        }} className="configure-voice-modal">
            <Modal.Header closeButton closeLabel='Close'>
                <span style={{color: 'var(--bs-emphasis-color)', fontSize: 18, fontWeight: 'bold'}}>
                    {title}
                </span>
            </Modal.Header>
            <Modal.Body className='row'>
                {children}
                <div className='col-md-6'>
                    <VolumeSlider />
                    <SpeedSlider voice={voice} />
                    <PitchDropdown voice={voice} />
                    <StylesDropdown voice={voice} />
                </div>
                <div className="col-md-6">
                    <VoicePreviewTextArea />
                </div>
            </Modal.Body>
            <Modal.Footer style={{display: 'flex', justifyContent: 'center'}}>
                <button disabled={isSubmitting} onClick={() => onSubmit()} className='btn btn-primary'>
                    {
                        isSubmitting
                        ? <Spinner color="white" />
                        : <>{submitButtonText}</>}
                </button>
            </Modal.Footer>
        </Modal>
    )
}

const VoicePreviewTextArea = () => {

    const selectedVoice = useAIVoicesStore((state) => state.selectedVoice);
    const voiceConfiguration = useVoiceConfigurationStore((state) => state.voiceConfiguration)
    const { mutateAsync: generatePreview, isLoading: isLoadingAudioPreview } = useAudioPreview();

    const [text, setText] = useState("If you set your goals ridiculously high and it's a failure, you will fail above everyone else's success.");

    const [audio, setAudio] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Emotional-voice previews resolve asynchronously (up to 45s) over a websocket;
    // guard against setting state after the modal has been closed mid-generation.
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const onPlayPreview = async () => {

        if (!selectedVoice || isLoadingAudioPreview) {
            return;
        }

        if(text.length < 10){
            toast.error("Preview text cannot be less than 10 characters");
            return;
        }

        const isStyleAvailable = !isStyleDisabled(selectedVoice, selectedVoice);
        const isPitchAvailable = !isPitchDisabled(selectedVoice, selectedVoice);
        const isSpeedAvailable = !isSpeedDisabled(selectedVoice);

        const isEmotionalVoice = selectedVoice.engine === 'emotional';

        const profile = {
            voice: selectedVoice.code,
            lang_code: selectedVoice.langCode,
            platform: selectedVoice.platform,
            engine: selectedVoice.engine,
            volume: voiceConfiguration.volume,
            ...(isSpeedAvailable && {speed: voiceConfiguration.speed}),
            ...((isStyleAvailable && voiceConfiguration.style !== 'default') && {style: voiceConfiguration.style}),
            ...((isPitchAvailable && voiceConfiguration.pitch !== 'default') && {pitch: voiceConfiguration.pitch}),
            // Chatterbox/emotional voices need a reference audio sample to clone from.
            ...(isEmotionalVoice && selectedVoice.audio && {audio: selectedVoice.audio}),
            ...(isEmotionalVoice && selectedVoice.metadata && {metadata: selectedVoice.metadata}),
        }

        try {
            const response = await generatePreview({ profile, text });
            if (!isMountedRef.current) return;
            setAudio(response.data.audio_url);
            setIsPlaying(true);
        } catch (error) {
            if (!isMountedRef.current) return;
            const message = error instanceof Error ? error.message : "Something went wrong while generating audio preview.";
            toast.error(message);
            Sentry.captureException(error);
        }
    }

    return (
        <>
            <div className="pb-0 mb-3">
                <p className="mb-2" style={{color : "#6A6A6A", fontWeight : 500}}>Test your voice settings</p>
                <textarea style={{resize: 'none', fontSize : 12}} className="form-control sample-text" rows={6} maxLength={200} onChange={(event) => setText(event.target.value)} defaultValue={text} />
                <p className="text-end mb-0"><small className="fst-italic">{200 - text.length} characters left</small></p>
            </div>
            {
                isLoadingAudioPreview
                ? (
                    <button type="button" className="btn btn--play-preview">
                        <Spinner color="var(--bs-secondary-color)" />
                    </button>
                )
                : (
                    (isPlaying)?
                        (
                            <button type="button" className="btn btn--play-preview" onClick={()=>{setIsPlaying(false)}} ><img className="me-2" src={pausePreviewIcon} alt="play icon"/> Pause Preview</button>
                        )
                        :
                        (
                            <button type="button" className="btn btn--play-preview" onClick={onPlayPreview} ><img className="me-2" src={playPreviewIcon} alt="play icon"/> Play Preview</button>
                        )
                )
            }
            {
                (audio !== null && audio !== undefined) &&
                <ReactHowler src={audio} playing={isPlaying} onEnd={()=>{setIsPlaying(false)}} />
            }
        </>
    )
}
